<template>
  <modal
    v-if="isOpen"
    :title="$t('DistributedHistory.title')"
    @close-request="onModalClose()"
    closable
  >
    <div class="">
      <div class="text-center">
        <i class="red-text"><i class="fa fa-exclamation mr-1" /> {{$t('DistributedHistory.alert_detail')}}</i>
      </div>

      <div>
        <!-- History -->
        <transfer-history
          :history="order.historySync"
          :order="order"
          :orderId="orderId"
          :priceOrderLink="priceOrderLink"
          :title="$t('OrderInfo.delivery.title')"
          class="mx-2"
          @onOrderModification="onOrderModification"
        />

        <modification-delivery-modal 
          ref="modifDelivModal"
          v-show="false"
          :entry="{}"
          :historyKey="historyKey"
          :isOpen="false"
          :orderId="orderId"
          :priceOrderLink="priceOrderLink"
          :isSetCssFeedback="false"
        />

        <transition name="fade" v-if="isShowDP">
          <div class="mt-3 center">
            <p v-if="isShowDescription && !isPickupMode" class="text-center">{{$t('DistributedHistory.drop_date_description')}}</p>
            <p v-if="isShowDescription && isPickupMode" class="text-center">{{$t('DistributedHistory.pick_date_description')}}</p>
            <custom-date-pick
              class="vdpCalendar"
              :formatPRP="'YYYY/MM/DD'"
              :displayFormatPRP="''"
              :pickTimePRP="false"
              :use12HourClockPRP="false"
              :hasInputElement="false"
              @updateDatePick="updateDropDate"
            />
          </div>
        </transition>
      </div>

      <div class="mt-2 text-center">
        <p class="red-text">{{feedbackMessage}}</p>
        <button v-if="!isShowDP" class="btn btn-kne-green" @click="onValidOrderShowDP()">{{$t('DistributedHistory.approved_order')}}</button>
        <button v-if="!isShowDP" class="btn btn-kne-orange ml-2" @click="onValidPickupShowDP()">{{$t('DistributedHistory.approved_pickup')}}</button>
        <button v-if="isShowDP" class="btn btn-kne-green" @click="onConfirmDate(order, selectedDate, orderId, historyKey)">{{$t('General.confirm')}}</button>
        <button v-if="!isShowDP" class="btn btn-kne-red" @click="onInvalidOrder(orderId, historyKey)">{{$t('DistributedHistory.cancelled_order')}}</button>
      </div>

    </div>
  </modal>
</template>

<script>
import TransferHistory from '../Other-components/TransferHistory.vue'
import HistoryPage from '../Other-components/HistoryPage.vue'
import Modal from '../../interface-components/Modal.vue'
import CustomDatePick from '../../special-components/Custom-packages/Custom-DatePick.vue'
import ModificationDeliveryModal from '../Modal-templates/ModificationDelivery-modal.vue'
import { db } from '../../../../firebase'
import { publishOrderSyncRequest } from '../../../../odoo-sync-requests/order'

export default {
  name: 'DistributedHistoryModal',
  components: {
    Modal,
    CustomDatePick,
    HistoryPage,
    ModificationDeliveryModal,
    TransferHistory
  },

  props: {
    isOpen: Boolean,
    isShowDescription: {
      type: Boolean,
      required: false,
      default: true
    },
    priceOrderLink: {
      type: Array,
      required: true
    },
    isSetCssFeedback: { // Determines if the CSS should be set for price inputs (Need this because of "StatusChangeModal")
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      arrOfCancelledOrders: [],
      isShowDP: false,
      isPickupMode: false,
      selectedDate: '', // Timestamp format
      feedbackMessage: '',
      order: {}, // Order is set from OrderCurrent.vue
      orderId: '', // For HistoryPage.vue
      historyKey: ''
    }
  },

  methods: {
    onOrderModification () {
      this.$emit('onOrderModification')
    },

    getCancellingUserName () {
      const user = this.userInfo || {}
      const firstName = typeof user.firstName === 'string' ? user.firstName.trim() : ''
      const lastName = typeof user.lastName === 'string' ? user.lastName.trim() : ''
      const fullName = `${firstName} ${lastName}`.trim()

      if (fullName) return fullName
      if (typeof user.name === 'string' && user.name.trim()) return user.name.trim()
      if (typeof this.uid === 'string' && this.uid.trim()) return this.uid

      return 'Unknown'
    },

    appendCancellationComment (currentComment) {
      const note = `Cancelled by (${this.getCancellingUserName()})`
      const comment = typeof currentComment === 'string' ? currentComment.trim() : ''

      if (!comment) return note
      if (comment.includes(note)) return comment

      return `${comment}\n${note}`
    },

    /** Show the DatePick window for delivery */
    onValidOrderShowDP () {
      this.isPickupMode = false
      this.isShowDP = true
    },

    /** Show the DatePick window for pickup */
    onValidPickupShowDP () {
      this.isPickupMode = true
      this.isShowDP = true
    },

    /** Check that a date has been selected
     *  If no @historyKey - push a new history to Firebase and get @historyKey
     *  Update the @dropDate property in Firebase
     *  Close modal
     */
    async onConfirmDate (order, selectedDate, orderId, historyKey) {
      if (selectedDate === '') {
        this.feedbackMessage = this.$t('DistributedHistory.select_date_error')
        return
      }

      if (this.checkIsHistoryGood(historyKey)) historyKey = this.getNewHistoryKeyFirebase(orderId, order) // If the historyKey parameter is empty (No history), create a history

      if (this.isPickupMode) {
        this.setPickDateFirebase(selectedDate)
      } else {
        this.setDropDateFirebase(selectedDate)
      }
      publishOrderSyncRequest(this.orderId, this.companyUID)
      order = await this.getOrder()
      this.closeModal(orderId, historyKey, order)
    },

    /** Create and return 'historyKey' to use in the Firebase path in following functions */
    getNewHistoryKeyFirebase (order) {
      const historyKey = this.historyFirebasePath.push(order).key
      return historyKey
    },

    /** Update property 'dropDate'  in Firebase path */
    async setDropDateFirebase (date) {
      const updatedDataName = 'dropDate'

      this.setFirebaseData(this.historyKeyFirebasePath, updatedDataName, date)
      publishOrderSyncRequest(this.orderId, this.companyUID)
      this.setIsCancelled(false)
    },

    /** Update property 'pickDate' in Firebase path */
    async setPickDateFirebase (date) {
      const updatedDataName = 'pickDate'

      this.setFirebaseData(this.historyKeyFirebasePath, updatedDataName, date)
      publishOrderSyncRequest(this.orderId, this.companyUID)
      this.setIsCancelled(false)
    },

    /** Update property 'isCancelled' in Firebase
     *  Close modal
     */
    async onInvalidOrder (orderId, historyKey) {
      let updateOrderObj = {
        numRef: '',
        comment: '',
        numBill: '',
        price: 0,
        prix: 0,
        isCancelled: true
      }

      const historyEntry = this.order && this.order.historySync && this.order.historySync[historyKey]
      if (historyEntry) {
        updateOrderObj.numRef = historyEntry.numRef || ''
        updateOrderObj.comment = this.appendCancellationComment(historyEntry.comment || '')
        updateOrderObj.numBill = historyEntry.numBill || ''
      }

      await this.updateFirebaseData(this.historyKeyFirebasePath, updateOrderObj)
      publishOrderSyncRequest(this.orderId, this.companyUID)
      const order = await this.getOrder()
      this.closeModal(orderId, historyKey, order)
    },

    updateFirebaseData (firebasePath, updatedDataObj) {
      return firebasePath.update(updatedDataObj).catch((error) => {
        console.log('Error found in updating firebase data', error)
        throw error
      })
    },

    /** Update the property 'isCancelled' in Firebase path */
    setIsCancelled (isTruthy) {
      const updatedDataName = 'isCancelled'
      this.setFirebaseData(this.historyKeyFirebasePath, updatedDataName, isTruthy)
      publishOrderSyncRequest(this.orderId, this.companyUID)
    },

    setFirebaseData (firebasePath, updatedDataName, updatedDataValue) {
      firebasePath.child(updatedDataName).set(updatedDataValue).then(() => {
      }).catch((error) => {
        console.log('Error found in setting firebase data', error)
      })
    },

    /** Gets the updated order to refresh the render in OrderCurrent after updating @dropDate or/and @isCancelled */
    async getOrder () {
      let order = {}

      await this.historyKeyFirebasePath.once('value').then(snapshot => {
        order = snapshot.val()
      })

      return order
    },

    /** Reset DatePick window
     *  Clear @feedbackMessage
     *  Close Modal
     *  Refreshes the render in OrderCurrent
    */
    closeModal (orderId, historyKey, order) {
      this.resetDP()
      this.clearFeedbackMessage()
      this.onModalClose()
      this.$emit('updateOrderInSelectedFull', orderId, historyKey, order)
    },

    resetDP () {
      this.isShowDP = false
      this.isPickupMode = false
      this.selectedDate = ''
    },

    clearFeedbackMessage () {
      this.feedbackMessage = ''
    },

    onModalClose () {
      this.resetDP()
      this.clearFeedbackMessage()
      this.$emit('closeModal')
    },

    /** From @emits Custom-DatePick.vue (custom-date-pick) */
    updateDropDate (timestamp) {
      this.selectedDate = timestamp
    },

    /** Used to set the red (false) or green (true) buttons in Short Dispatch */
    checkHistory (history) {
      if (!this.checkIsHistoryGood(history)) return true // If there is no History

      const objKeys = this.getObjectKeys(history)
      const lastObjOfHistory = this.getLastObjectFromArray(history, objKeys)

      if (!this.checkIsUndefined(lastObjOfHistory.isCancelled)) return true // If the last history object has isCancelled
      if (!this.checkIsUndefined(lastObjOfHistory.dropDate)) return true // If the last history object has a dropDate

      return false
    },

    /** Gets all Key names of an object */
    getObjectKeys (object) {
      if (this.checkIsUndefined(object)) return false

      const objKeys = Object.keys(object)
      return objKeys
    },

    checkIsHistoryGood (history) {
      if (typeof history === 'undefined' || typeof history === 'string') return false
      else return true
    },

    /** From an Array, return the last object */
    getLastObjectFromArray (object, objKeys) {
      const lastObj = object[this.getLastArrayData(objKeys)]
      return lastObj
    },

    /** - Function Name */
    getLastArrayData (array) {
      const data = array[array.length - 1]
      return data
    },

    /** Called from @ref in OrderCurrent, did it like this to have the methods contained in @DistributedHistoryModal */
    setOrder (order) {
      this.order = order
    },

    /** Called from @ref in OrderCurrent */
    setOrderId (orderId) {
      this.orderId = orderId
    },

    /** Called from @ref in OrderCurrent */
    setHistoryKey (historyKey) {
      this.historyKey = historyKey
    }
  },

  computed: {
    historyFirebasePath () {
      return db.ref(`database/transporter/${this.companyUID}/history/${this.orderId}`)
    },

    historyKeyFirebasePath () {
      return this.historyFirebasePath.child(this.historyKey)
    },

    checkDate () {
      if (typeof this.date !== 'undefined' && this.date !== '') return true
      else return false
    },

    uid () {
      return this.$store.state.user.uid
    },

    userInfo () {
      return this.$store.state.userInfo
    },

    companyUID () {
      return this.$store.state.authorization.company
    }
  }
}
</script>

<style scoped>
  .center {
    display: flex;
    flex-flow: column;
    justify-content: center;
  }
  .vdpCalendar {
    align-items: center;
  }
</style>
